import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, from } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../../core/global-message';
import { GlobalMessageActions } from '../../../../../core/global-message/store/actions';
import { normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { AppActions } from '../../../../../core/util/store/actions';
import { CartEntryPrePrintedLabelConnector } from '../../connector/cart-entry-pre-printed-label.connector';
import { CartEntryPrePrintedLabelActionTypes, UpdatePrePrintedLabelFail } from '../actions/cart-entry-pre-printed-label.action';
import { CartActions } from '../actions/index';

@Injectable()
export class CartEntryPrePrintedLabelEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  loadOptions$: Observable<CartActions.LoadPrePrintedOptionsSuccess | CartActions.LoadPrePrintedOptionsFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPrePrintedLabelActionTypes.LoadPrePrintedOptions),
      map((action: CartActions.LoadPrePrintedOptions) => action.payload),
      concatMap((payload) =>
        this.connector.loadOptions(payload.userId, payload.cartId, payload.entry).pipe(
          map((data) => {
            return new CartActions.LoadPrePrintedOptionsSuccess({ ...payload, options: data });
          }),
          catchError((error) =>
            from([
              new CartActions.LoadPrePrintedOptionsFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
            ])
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  loadInnerPackageQuantityRequired$: Observable<
    CartActions.LoadPrePrintedInnerPackageQuantityRequiredSuccess | CartActions.LoadPrePrintedInnerPackageQuantityRequiredFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPrePrintedLabelActionTypes.LoadPrePrintedInnerPackageQuantityRequired),
      map((action: CartActions.LoadPrePrintedOptions) => action.payload),
      concatMap((payload) =>
        this.connector.loadInnerPackageQuantityRequired(payload.userId, payload.cartId, payload.entry).pipe(
          map((data) => {
            return new CartActions.LoadPrePrintedInnerPackageQuantityRequiredSuccess({
              ...payload,
              innerPackageQuantityRequired: data.innerPackageQuantityRequired,
            });
          }),
          catchError((error) =>
            from([
              new CartActions.LoadPrePrintedInnerPackageQuantityRequiredFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
            ])
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateSelectedOption$: Observable<CartActions.UpdatePrePrintedLabelSuccess | UpdatePrePrintedLabelFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPrePrintedLabelActionTypes.UpdatePrePrintedLabel),
      map((action: CartActions.UpdatePrePrintedLabel) => action.payload),
      concatMap((payload) => {
        return this.connector.update(payload.userId, payload.cartId, payload.entry, payload.option).pipe(
          map(() => {
            return new CartActions.UpdatePrePrintedLabelSuccess({
              ...payload,
            });
          }),
          catchError((error) =>
            from([
              new CartActions.UpdatePrePrintedLabelFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
            ])
          )
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  updateSelectedOptionSuccess$: Observable<CartActions.LoadCart | GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPrePrintedLabelActionTypes.UpdatePrePrintedLabelSuccess),
      map((action: CartActions.UpdatePrePrintedLabelSuccess) => action.payload),
      switchMap((payload) => {
        return [
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          }),
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.prePrintedLabel.updateSelectedOptionSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          }),
        ];
      }),
      withdrawOn(this.contextChange$)
    )
  );

  updateSelectedOptionFailure$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPrePrintedLabelActionTypes.UpdatePrePrintedLabelFail),
      map((action: CartActions.UpdatePrePrintedLabelFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.prePrintedLabel.updateSelectedOptionFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  deleteSelectedOption$: Observable<CartActions.DeletePrePrintedLabelSuccess | CartActions.DeletePrePrintedLabelFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryPrePrintedLabelActionTypes.DeletePrePrintedLabel),
        map((action: CartActions.DeletePrePrintedLabel) => action.payload),
        concatMap((payload) => {
          return this.connector.delete(payload.userId, payload.cartId, payload.entry).pipe(
            map(() => {
              return new CartActions.DeletePrePrintedLabelSuccess({
                ...payload,
              });
            }),
            catchError((error) =>
              from([
                new CartActions.DeletePrePrintedLabelFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
              ])
            )
          );
        }),
        withdrawOn(this.contextChange$)
      )
    );

  deleteSelectedOptionSuccess$: Observable<CartActions.LoadCart | GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPrePrintedLabelActionTypes.DeletePrePrintedLabelSuccess),
      map((action: CartActions.UpdatePrePrintedLabelSuccess) => action.payload),
      switchMap((payload) => {
        return [
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          }),
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.prePrintedLabel.deleteSelectedOptionSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          }),
        ];
      }),
      withdrawOn(this.contextChange$)
    )
  );

  deleteSelectedOptionFailure$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPrePrintedLabelActionTypes.DeletePrePrintedLabelFail),
      map((action: CartActions.DeletePrePrintedLabelFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.prePrintedLabel.deleteSelectedOptionFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  constructor(private actions$: Actions, private connector: CartEntryPrePrintedLabelConnector) {}
}
